import type React from 'react';
import type { Theme } from '@mui/material/styles';
import { toHourMinute } from '../../utils/dateUtils';
import { registeredAriaLabel } from './RegisteredMarker';

/**
 * [Web UX 9 / T5, 2026-10-13] A naptár billentyűzetes és képernyőolvasós használhatósága — KÖZÖS
 * segédek a heti, napi és havi nézethez, hogy a három nézet ne egyenként (és eltérően) döntsön.
 *
 * MI VOLT A BAJ (2. körös UX-lista (`Documents/WEB_UX_JAVASLATOK_2KOR_2026-10-04.md`) 6.1–6.2):
 *  - `WeeklyCalendar.tsx:337-345` (és a havi/napi/edzői-elérhetőség lapozók): címke nélküli `IconButton`-ok
 *    — a képernyőolvasó csak annyit mondott: „gomb"; telefonon kb. 30–34 px-es érintési felület.
 *  - A heti kártya (`WeeklyCalendar.tsx:440`, `onClick`-es `Box`) és a napi kártya (`DailyTimeline.tsx:386`,
 *    `onClick`-es `Card`) billentyűzetről ELÉRHETETLEN volt (sem `tabIndex`, sem `role`, sem Enter/Space),
 *    és az akadálymentes nevükből hiányzott a „Jelentkeztél" állapot (a havi chipnél már benne volt:
 *    `MonthlyCalendar.tsx` → `registeredAriaLabel`).
 */

/** Telefonon (xs) legalább 44×44 px érintési felület a lapozó-gombokra; asztalon a megszokott méret. */
export const NAV_BUTTON_TOUCH_SX = {
  minWidth: { xs: 44, sm: 0 },
  minHeight: { xs: 44, sm: 0 },
} as const;

/**
 * Látható fókusz-gyűrű (csak billentyűzetes fókusznál, `:focus-visible`). Kétszínű: kívül 2 px
 * `text.primary` (világosban közel fekete, sötétben közel fehér), a kártya és a gyűrű között 2 px
 * `background.paper` sáv. Így a gyűrű a KÁRTYA színétől (az eseménytípus tetszőleges színe) függetlenül
 * látszik: a nap-oszlop / idővonal háttere (`background.paper`) mellett a `text.primary` mindkét témában
 * magas kontrasztú.
 */
export const cardFocusRingSx = {
  '&:focus-visible': {
    outline: (theme: Theme) => `2px solid ${theme.palette.text.primary}`,
    outlineOffset: '2px',
    boxShadow: (theme: Theme) => `0 0 0 2px ${theme.palette.background.paper}`,
  },
} as const;

/**
 * A havi chip változata: a chip egy `overflow: auto` tárolóban ül, a kifelé rajzolt gyűrűt levágná,
 * ezért BEFELÉ rajzolunk — kívül 2 px `text.primary`, belül 2 px `background.paper` sáv (kétszínű,
 * így a chip tetszőleges háttérszínén is látszik).
 */
export const chipFocusRingSx = {
  '&.Mui-focusVisible, &:focus-visible': {
    outline: (theme: Theme) => `2px solid ${theme.palette.text.primary}`,
    outlineOffset: '-2px',
    boxShadow: (theme: Theme) => `inset 0 0 0 4px ${theme.palette.background.paper}`,
  },
} as const;

/**
 * A kártya összefoglaló akadálymentes neve: „<név>, <kezdés>–<vég>, <helyszín>[, <további>…]", és
 * jelentkezett eseménynél elé a „Jelentkeztél: " — ugyanaz a szerkezet, mint a havi chipé
 * (`registeredAriaLabel`). Csak azt mondja ki, amit a kártya is mutat (a hívó adja az `extra` részt).
 */
export function eventCardAriaLabel(args: {
  eventName: string;
  startTime: string;
  endTime: string;
  venueName?: string | null;
  extra?: Array<string | null | undefined | false>;
  registered: boolean;
}): string {
  const parts = [
    args.eventName,
    `${toHourMinute(args.startTime)}–${toHourMinute(args.endTime)}`,
    args.venueName || null,
    ...(args.extra ?? []),
  ].filter((p): p is string => typeof p === 'string' && p.length > 0);
  const base = parts.join(', ');
  return args.registered ? registeredAriaLabel(base) : base;
}

/**
 * `role="button"`-os, nem-natív elem billentyűzetes aktiválása, a natív gomb mintájára:
 * Enter a lenyomáskor, Szóköz a FELENGEDÉSKOR aktivál (a Szóköz lenyomásánál `preventDefault`,
 * hogy ne görgessen). A Szóköz felengedésre aktiválása szándékos: ha már a lenyomásra nyílna a
 * részletező ablak, a felengedés az ablakba került fókuszon (pl. egy gombon) futhatna le.
 * Csak magára az elemre reagál (`target === currentTarget`), a belső elemekről felbuborékoló
 * billentyűkre nem.
 *
 * [Web UX 9 / T5, 2026-10-13, validátor M1] A Szóköz felengedése CSAK akkor aktivál, ha a lenyomás is
 * EZEN az elemen történt (a natív gomb így viselkedik). Mért hiba (`calendarA11y.ts:100` korábbi
 * `onKeyUp`-ja): Szóköz lenyomva a „Mai nap" gombon, fókusz a kártyára, felengedés ott → megnyílt a
 * részletező ablak. Ezért a lenyomás jelzőt tesz az elemre (`spacePressed`), a felengedés csak jelzővel
 * aktivál, és a jelző felengedéskor ÉS fókuszvesztéskor (`onBlur`) törlődik.
 */
const spacePressed = new WeakSet<EventTarget>();

export function buttonKeyHandlers(activate: () => void): {
  onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
  onKeyUp: (e: React.KeyboardEvent<HTMLElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLElement>) => void;
} {
  return {
    onKeyDown: (e) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        activate();
      } else if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        e.stopPropagation();
        spacePressed.add(e.currentTarget);
      }
    },
    onKeyUp: (e) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        e.stopPropagation();
        // Csak az ezen az elemen kezdett Szóköz-lenyomás aktivál (lásd fent, validátor M1).
        if (!spacePressed.delete(e.currentTarget)) return;
        activate();
      }
    },
    onBlur: (e) => {
      spacePressed.delete(e.currentTarget);
    },
  };
}
